import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { TrashIcon } from '@heroicons/react/24/outline';
import Card from '../../components/ui/Card';
import Button from '../../components/ui/Button';
import Select from '../../components/forms/Select';
import { Spinner } from '../../components/ui/Loading';
import { H1, Body } from '../../components/ui/Typography';
import {
  useGuardianNotifications,
  useGuardianMarkAsRead,
  useGuardianMarkAllAsRead,
  useGuardianDeleteNotification,
} from '../../hooks/api/useGuardianNotifications';
import { Notification } from '../../types/api';
import { formatDateTime, formatRelative } from '../../utils/date';
import { getNotificationStyle } from '../../components/notifications/notificationStyles';

const READ_OPTIONS = [
  { value: '', label: 'All' },
  { value: 'false', label: 'Unread only' },
  { value: 'true', label: 'Read only' },
];

const PAGE_SIZE = 25;

export const GuardianNotifications: React.FC = () => {
  const navigate = useNavigate();
  const [readFilter, setReadFilter] = useState('');
  const [page, setPage] = useState(0);

  const filters = useMemo(
    () => ({
      is_read:
        readFilter === ''
          ? undefined
          : readFilter === 'true'
          ? true
          : false,
      skip: page * PAGE_SIZE,
      limit: PAGE_SIZE,
    }),
    [readFilter, page]
  );

  const { data, isLoading, isError } = useGuardianNotifications(filters);
  const markAsRead = useGuardianMarkAsRead();
  const markAllAsRead = useGuardianMarkAllAsRead();
  const deleteNotification = useGuardianDeleteNotification();

  const items = data?.items ?? [];
  const total = data?.total ?? 0;
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  const handleRowClick = (n: Notification) => {
    if (!n.is_read) {
      markAsRead.mutate(n.id);
    }
    if (n.link) {
      navigate(n.link);
    }
  };

  const handleDelete = (
    event: React.MouseEvent<HTMLButtonElement>,
    id: string
  ) => {
    event.stopPropagation();
    deleteNotification.mutate(id);
  };

  return (
    <div className="p-4 lg:p-6 max-w-4xl mx-auto">
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
      >
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-6 gap-3">
          <div>
            <H1 className="text-gray-900 dark:text-gray-100">Notifications</H1>
            <Body className="text-gray-600 dark:text-gray-400 mt-1">
              Updates about your children
            </Body>
          </div>
          <Button
            variant="secondary"
            onClick={() => markAllAsRead.mutate()}
            disabled={markAllAsRead.isPending}
          >
            Mark all read
          </Button>
        </div>

        <Card className="mb-6">
          <div className="w-full sm:w-64">
            <Select
              label="Status"
              value={readFilter}
              onChange={(e) => {
                setReadFilter(e.target.value);
                setPage(0);
              }}
              options={READ_OPTIONS}
            />
          </div>
        </Card>

        <Card className="!p-0 overflow-hidden">
          {isLoading ? (
            <div className="flex justify-center items-center py-16">
              <Spinner />
            </div>
          ) : isError ? (
            <div className="p-8 text-center text-red-600 dark:text-red-400">
              Unable to load notifications.
            </div>
          ) : items.length === 0 ? (
            <div className="p-8 text-center text-gray-500 dark:text-gray-400">
              No notifications found.
            </div>
          ) : (
            <ul className="divide-y divide-gray-100 dark:divide-gray-700">
              {items.map((n) => {
                const style = getNotificationStyle(n.type, n.icon);
                const unread = !n.is_read;
                return (
                  <li key={n.id}>
                    <div
                      role="button"
                      tabIndex={0}
                      onClick={() => handleRowClick(n)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          handleRowClick(n);
                        }
                      }}
                      className={`group flex items-start gap-4 px-4 sm:px-6 py-4 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700/60 transition-colors focus:outline-none focus:ring-2 focus:ring-primary-500 ${
                        unread ? 'bg-blue-50 dark:bg-blue-900/20' : ''
                      }`}
                    >
                      <div
                        className={`flex-shrink-0 w-10 h-10 rounded-lg flex items-center justify-center text-lg ${style.bg}`}
                        aria-hidden="true"
                      >
                        <span>{style.icon}</span>
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-start justify-between gap-2">
                          <p
                            className={`text-sm ${
                              unread
                                ? 'font-semibold text-gray-900 dark:text-gray-100'
                                : 'font-medium text-gray-800 dark:text-gray-200'
                            }`}
                          >
                            {n.title}
                          </p>
                          {unread && (
                            <span
                              className="flex-shrink-0 w-2 h-2 mt-2 rounded-full bg-primary-500"
                              aria-label="Unread"
                            />
                          )}
                        </div>
                        {n.message && (
                          <p className="text-sm text-gray-600 dark:text-gray-400 mt-0.5">
                            {n.message}
                          </p>
                        )}
                        <p
                          className="text-xs text-gray-500 dark:text-gray-500 mt-1"
                          title={formatDateTime(n.created_date)}
                        >
                          {formatRelative(n.created_date)}
                        </p>
                      </div>
                      <button
                        onClick={(e) => handleDelete(e, n.id)}
                        className="flex-shrink-0 p-1.5 text-gray-400 dark:text-gray-500 hover:text-red-600 dark:hover:text-red-400 opacity-0 group-hover:opacity-100 transition-opacity"
                        aria-label="Delete notification"
                      >
                        <TrashIcon className="w-4 h-4" />
                      </button>
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </Card>

        {totalPages > 1 && (
          <div className="flex items-center justify-between mt-4">
            <Body className="text-sm text-gray-600 dark:text-gray-400">
              Page {page + 1} of {totalPages} · {total} total
            </Body>
            <div className="flex gap-2">
              <Button
                variant="secondary"
                size="sm"
                onClick={() => setPage((p) => Math.max(0, p - 1))}
                disabled={page === 0}
              >
                Previous
              </Button>
              <Button
                variant="secondary"
                size="sm"
                onClick={() =>
                  setPage((p) => Math.min(totalPages - 1, p + 1))
                }
                disabled={page >= totalPages - 1}
              >
                Next
              </Button>
            </div>
          </div>
        )}
      </motion.div>
    </div>
  );
};

export default GuardianNotifications;
